import { Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useRef, useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';

interface TruncatedTextProps {
  text: string;
  maxWidth?: number | string | object;
  sx?: object;
  variant?: any;
  /**
   * 1 = una sola línea con ellipsis (comportamiento anterior).
   * 2+ = varias líneas con `-webkit-line-clamp`; tooltip solo si el texto se corta.
   */
  lineClamp?: number;
  /**
   * Modo tablet: en viewports menores a `md` (tablet/iPad) muestra el texto completo
   * con salto de línea, sin ellipsis ni tooltip. En desktop mantiene truncamiento y
   * tooltip solo cuando el texto no entra. Usado en tablas de tickets/tipos de ticket.
   */
  tabletWrap?: boolean;
}

export default function TruncatedText({
  text,
  maxWidth,
  sx = {},
  variant = 'subtitle2',
  lineClamp = 1,
  tabletWrap = false
}: TruncatedTextProps) {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [open, setOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMultiline = lineClamp > 1;
  const useTabletWrapMode = tabletWrap && isTablet;

  const checkTruncation = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    if (isMultiline) {
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    } else {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [text, isMultiline]);

  useEffect(() => {
    if (useTabletWrapMode) return;

    checkTruncation();
    const el = textRef.current;
    if (!el) return;

    window.addEventListener('resize', checkTruncation);
    const observer = new ResizeObserver(() => checkTruncation());
    observer.observe(el);

    return () => {
      window.removeEventListener('resize', checkTruncation);
      observer.disconnect();
    };
  }, [checkTruncation, useTabletWrapMode]);

  if (text === '' || text == null) {
    return null;
  }

  // Modo tablet: texto completo con wrap, sin truncar ni tooltip
  if (useTabletWrapMode) {
    return (
      <Typography
        ref={textRef}
        variant={variant as any}
        component="span"
        sx={{
          display: 'block',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          minWidth: 0,
          width: '100%',
          maxWidth: '100%',
          ...sx
        }}
      >
        {text}
      </Typography>
    );
  }

  const multilineSx = isMultiline
    ? {
        display: '-webkit-box',
        WebkitLineClamp: lineClamp,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
        whiteSpace: 'normal' as const,
        wordBreak: 'break-word' as const,
        overflowWrap: 'break-word' as const
      }
    : {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
        display: 'block'
      };

  const typography = (
    <Typography
      ref={textRef}
      variant={variant as any}
      {...(!isMultiline ? { noWrap: true } : {})}
      sx={{
        ...multilineSx,
        maxWidth: maxWidth ?? (isMultiline ? '100%' : { xs: 200, sm: 'none' }),
        cursor: isMobile && isTruncated ? 'pointer' : 'default',
        ...sx
      }}
      onClick={() => {
        if (isMobile && isTruncated) setOpen((prev) => !prev);
      }}
    >
      {text}
    </Typography>
  );

  return isTruncated ? (
    <Tooltip
      title={text}
      arrow
      open={isMobile ? open : undefined}
      onClose={() => setOpen(false)}
    >
      {typography}
    </Tooltip>
  ) : (
    typography
  );
}
